import React, {useState, useCallback, useRef, useEffect} from 'react'

export type ZoomPanState = {
  zoom: number
  panX: number
  panY: number
}

type DragStart = {
  x: number
  y: number
  panX: number
  panY: number
}

type UseMapZoomPanOptions = {
  minZoom?: number
  maxZoom?: number
  zoomStep?: number
  initialZoom?: number
  initialPanX?: number
  initialPanY?: number
}

type UseMapZoomPanReturn = {
  zoom: number
  panX: number
  panY: number
  containerRef: React.RefObject<HTMLDivElement>
  handleWheel: (e: React.WheelEvent) => void
  handleMouseDown: (e: React.MouseEvent) => void
  handleMouseMove: (e: React.MouseEvent) => void
  handleMouseUp: () => void
  handleMouseLeave: () => void
  handleTouchStart: (e: React.TouchEvent) => void
  handleTouchMove: (e: React.TouchEvent) => void
  handleTouchEnd: () => void
  resetView: () => void
  zoomIn: () => void
  zoomOut: () => void
  isDragging: boolean
}

type CalculateZoomStateParams = {
  prev: ZoomPanState
  mouseX: number
  mouseY: number
  deltaY: number
  minZoom: number
  maxZoom: number
  zoomStep: number
}

export const calculateZoomState = ({
  prev,
  mouseX,
  mouseY,
  deltaY,
  minZoom,
  maxZoom,
  zoomStep
}: CalculateZoomStateParams): ZoomPanState => {
  const direction = deltaY < 0 ? 1 : -1
  const newZoom = Math.min(maxZoom, Math.max(minZoom, prev.zoom + direction * zoomStep))

  if (newZoom === prev.zoom) return prev

  const zoomRatio = newZoom / prev.zoom
  return {
    zoom: newZoom,
    panX: mouseX - (mouseX - prev.panX) * zoomRatio,
    panY: mouseY - (mouseY - prev.panY) * zoomRatio
  }
}

export const useMapZoomPan = (options: UseMapZoomPanOptions = {}): UseMapZoomPanReturn => {
  const {
    minZoom = 1,
    maxZoom = 4,
    zoomStep = 0.15,
    initialZoom = 1,
    initialPanX = 0,
    initialPanY = 0
  } = options

  const [state, setState] = useState<ZoomPanState>({
    zoom: initialZoom,
    panX: initialPanX,
    panY: initialPanY
  })

  const [isDragging, setIsDragging] = useState(false)
  const dragStart = useRef<DragStart | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const startDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (state.zoom <= 1) return
      setIsDragging(true)
      dragStart.current = {x: clientX, y: clientY, panX: state.panX, panY: state.panY}
    },
    [state.zoom, state.panX, state.panY]
  )

  const moveDrag = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging || !dragStart.current) return
      const {x, y, panX: startPanX, panY: startPanY} = dragStart.current
      setState((prev) => ({...prev, panX: startPanX + clientX - x, panY: startPanY + clientY - y}))
    },
    [isDragging]
  )

  const endDrag = useCallback(() => {
    setIsDragging(false)
    dragStart.current = null
  }, [])

  // Zoom centered on mouse position
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      const container = containerRef.current
      if (!container) return

      const rect = container.getBoundingClientRect()
      const mouseX = e.clientX - rect.left
      const mouseY = e.clientY - rect.top

      setState((prev) =>
        calculateZoomState({
          prev,
          mouseX,
          mouseY,
          deltaY: e.deltaY,
          minZoom,
          maxZoom,
          zoomStep
        })
      )
    },
    [maxZoom, minZoom, zoomStep]
  )

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      startDrag(e.clientX, e.clientY)
    },
    [startDrag]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => moveDrag(e.clientX, e.clientY),
    [moveDrag]
  )

  const handleMouseUp = endDrag
  const handleMouseLeave = endDrag

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) startDrag(e.touches[0].clientX, e.touches[0].clientY)
    },
    [startDrag]
  )

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1) moveDrag(e.touches[0].clientX, e.touches[0].clientY)
    },
    [moveDrag]
  )

  const handleTouchEnd = endDrag

  const resetView = useCallback(() => {
    setState({zoom: initialZoom, panX: initialPanX, panY: initialPanY})
  }, [initialZoom, initialPanX, initialPanY])

  const zoomIn = useCallback(() => {
    setState((prev) => ({
      ...prev,
      zoom: Math.min(maxZoom, prev.zoom + zoomStep * 2)
    }))
  }, [maxZoom, zoomStep])

  const zoomOut = useCallback(() => {
    setState((prev) => {
      const newZoom = Math.max(minZoom, prev.zoom - zoomStep * 2)
      // Reset pan when zooming out to 1x
      if (newZoom <= 1) {
        return {zoom: newZoom, panX: 0, panY: 0}
      }
      return {...prev, zoom: newZoom}
    })
  }, [minZoom, zoomStep])

  // Prevent default wheel behavior on the container
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const preventScroll = (e: WheelEvent) => {
      e.preventDefault()
    }

    container.addEventListener('wheel', preventScroll, {passive: false})
    return () => container.removeEventListener('wheel', preventScroll)
  }, [])

  return {
    zoom: state.zoom,
    panX: state.panX,
    panY: state.panY,
    containerRef,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    resetView,
    zoomIn,
    zoomOut,
    isDragging
  }
}
